<template>
  <div class="container profile">
    <div v-if="isMe" class="row breadcrumb-row">
      <div class="col-12">
        <ul class="bread-crumbs">
          <li>
            <router-link to="/account"> My Account </router-link>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="icon alt"
            />
          </li>
          <li>
            <router-link :to="profileLink"> My Profile </router-link>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="icon alt"
            />
          </li>
        </ul>
      </div>
    </div>
    <div v-if="isMe" class="row">
      <div class="col-12">
        <router-link to="/account" tag="button" class="btn back-btn">
          <font-awesome-icon :icon="['fas', 'caret-left']" class="icon alt" />
          Back
        </router-link>
      </div>
    </div>

    <div v-if="!counselor" class="row">
      <Loader style="width: 100%" />
    </div>

    <div v-else class="row">
      <h1 class="sr-only">Counselor Profile</h1>
      <div class="col-12 col-lg-4">
        <div class="dashboard-card profile-block">
          <div class="avatar">
            <div
              v-if="avatarFileUrl"
              class="user-avatar"
              role="img"
              aria-label="Counselor Avatar Photo"
              :style="{ backgroundImage: `url(${avatarFileUrl})` }"
            />
            <div v-else class="user-avatar" />
            <h2>{{ counselor.user.fullName }}</h2>
            <p style="margin-bottom: 0">
              Counselor<span v-if="counselor.school">
                - {{ counselor.school.name }}</span>
            </p>
            <a
              v-if="counselor.email"
              :href="`mailto:${counselor.email}`"
              style="margin-bottom: 0; margin-top: 3px; display: block"
            >{{ counselor.email }}</a>
          </div>
          <hr />
          <ul class="info-list">
            <li v-if="counselor.office_phone">
              <font-awesome-icon
                :icon="['fas', 'phone-square']"
                class="icon alt"
              />
              <a :href="`tel:${counselor.office_phone}`">
                {{ counselor.office_phone }}
              </a>
            </li>
            <li>
              <button type="button" class="icon-btn" @click="sendMessage()">
                <font-awesome-icon
                  :icon="['fas', 'envelope-square']"
                  class="icon alt"
                />
                <span class="profile" style="color: white">Send a Message</span>
              </button>
            </li>
          </ul>
          <hr />
          <button
            type="button"
            class="btn btn-primary btn-wide btn-lg"
            style="width: 100%"
            @click="scheduleWithCounselor(counselor.id)"
          >
            Schedule
          </button>
        </div>
        <div
          v-if="
            counselor.accepted_insurance_carriers &&
              counselor.accepted_insurance_carriers.length
          "
          class="dashboard-card insurance-block"
        >
          <label>Insurance Accepted</label>
          <ul>
            <li v-for="insurance in counselor.accepted_insurance_carriers">
              {{ insurance.text }}
            </li>
          </ul>
        </div>
      </div>
      <div class="col-12 col-lg-8">
        <div v-if="counselor.vacationReturnDate" class="card vacation">
          <p>This counselor is currently on extended leave and will return on  {{ new Date(counselor.vacationReturnDate) | dateMonthDayOnly }}. </p>
          <p>Their response may be delayed during this time.</p>
        </div>
        <div class="dashboard-card light">
          <div class="row">
            <div class="col-8">
              <h2 class="mb-4">Bio/About</h2>
            </div>
            <div class="col-4 text-right">
              <router-link
                v-if="isMe"
                to="/account"
                tag="button"
                class="btn back-btn"
              >
                <font-awesome-icon :icon="['fas', 'edit']" class="icon alt" />
              </router-link>
            </div>
          </div>
          <p v-if="counselor.biography">{{ counselor.biography }}</p>
          <p v-else>This Counselor hasn't filled out their bio yet.</p>

          <h3 style="margin-top: 40px; margin-bottom: 20px; font-size: 1.5rem">
            More Info
          </h3>
          <table class="table bm-table">
            <tbody>
              <tr v-if="counselor.user.gender">
                <td>Gender Identity:</td>
                <td class="text-right">
                  {{ getCounselorGenderDisplayName(counselor.user.gender) }}
                </td>
              </tr>
              <tr>
                <td>Ethnicity:</td>
                <td class="text-right">
                  {{ counselor.user.race || "Preferred not to answer" | capitalize }}
                </td>
              </tr>
              <tr v-if="counselor.lgbtq">
                <td>Identifying as LGBTQIA+:</td>
                <td class="text-right">
                  {{ counselor.lgbtq ? 'Yes' : 'No' }}
                </td>
              </tr>
            </tbody>
          </table>

          <b v-if="counselor.languages.length" class="d-block mt-4">Languages</b>
          <ul v-if="counselor.languages.length" class="pills">
            <li v-for="s in counselor.languages">
              {{ s.name }}
            </li>
          </ul>

          <b v-if="counselor.specialties.length" class="d-block mt-4">Specialties</b>
          <ul v-if="counselor.specialties.length" class="pills">
            <li v-for="s in counselor.specialties">
              {{ s.display_name }}
            </li>
          </ul>

          <b v-if="counselor.registered_in_states.length" class="d-block mt-4">States Licensed to Practice</b>
          <ul v-if="counselor.registered_in_states.length" class="pills">
            <li v-for="s in counselor.registered_in_states">
              {{ s.display_name }}
            </li>
          </ul>
        </div>

        <div
          v-if="workingHours && workingHours.length"
          class="dashboard-card appt-table"
        >
          <h2>Weekly Availability</h2>
          <div class="table-responsive">
            <table class="table bm-table">
              <tr v-for="working in workingHours">
                <td>{{ working.day_of_the_week | capitalize }}s</td>
                <td class="text-right">
                  <span
                    v-for="period in working.periods"
                    v-if="period.formattedStartTime !== period.formattedEndTime"
                  >
                    {{ period.formattedStartTime }} -
                    {{ period.formattedEndTime }}<br />
                  </span>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>

    <send-message-modal ref="sendMessageModal" />
    <vue-accessible-modal>
      <template v-slot:backdrop />
    </vue-accessible-modal>
  </div>
</template>

<script>
import SessionRequestModal from '../students/session-request/SessionRequestModal.vue'
import UserClient from '@/api/UserClient';
import CounselorClient from '@/api/CounselorClient';
import SendMessageModal from '@/components/modals/SendMessageModal.vue';
import Loader from '@/components/Loader.vue';
import genderOptions from '@/helpers/mixins/gender-options';
import moment from 'moment-timezone';

export default {
  name: 'ViewCounselor',
  components: {
    Loader,
    SendMessageModal
  },
  mixins: [genderOptions],
  data() {
    return {
      userClient: new UserClient(this.$http),
      counselorClient: new CounselorClient(this.$http),
      counselor: null,
      avatarFileUrl: '',
      workingHours: null
    };
  },
  computed: {
    isMe() {
      return (
        this.$store.getters.currentUser &&
        this.$store.getters.currentUser.counselor_profile &&
        this.$store.getters.currentUser.counselor_profile.id ===
          parseInt(this.$route.params.id)
      );
    },
    profileLink() {
      if (this.isCounselor) {
        return `/counselor/view/${this.$store.getters.currentUser.counselor_profile.id}`;
      }

      if (this.isStudent) {
        return `/student/${this.$store.getters.currentUser.id}`;
      }

      return '';
    }
  },
  mounted() {
    this.userClient
      .getCounselorProfile(this.$route.params.id)
      .then((counselor) => {
        this.counselor = counselor;
        this.avatarFileUrl = this.counselor.user.photo
          ? this.counselor.user.photo.url
          : '';
      })
      .catch((ex) => {
        this.$router.push({
          name: 'dashboard',
          params: { from: 'counselor-not-found' }
        });
      });

    this.counselorClient
      .getWorkingHours(this.$route.params.id)
      .then((res) => {
        this.workingHours = res.working_hour_days;

        const local = moment();
        const localOffset = local.utcOffset();

        const tz = moment().tz(res.tzid);
        const workingOffset = tz.utcOffset();

        // offsets in minutes
        const minutesDelta = localOffset - workingOffset;

        this.workingHours.forEach((day) => {
          day.periods.forEach((period) => {
            const startTime = period.start_time;
            const endTime = period.end_time;

            const startTimeParts = startTime.split(':');
            const endTimeParts = endTime.split(':');

            let startTimeMinutes =
              parseInt(startTimeParts[0]) * 60 + parseInt(startTimeParts[1]);
            let endTimeMinutes =
              parseInt(endTimeParts[0]) * 60 + parseInt(endTimeParts[1]);

            let localStartTimeMinutes = startTimeMinutes + minutesDelta;
            let localEndTimeMinutes = endTimeMinutes + minutesDelta;

            if (localStartTimeMinutes < 0) {
              // todo: we went back to yesterday, so lets assume 00:00 for now.. ?
              localStartTimeMinutes = 0;
            }

            if (localEndTimeMinutes < 0) {
              // todo: we went back to yesterday, so lets assume 00:00 for now.. ?
              localEndTimeMinutes = 0;
            }

            if (localStartTimeMinutes > 1440) {
              // todo: we went over to tomorrow.. so lets cap it at 24:00 ...
              localStartTimeMinutes = 1440;
            }

            if (localEndTimeMinutes > 1440) {
              // todo: we went over to tomorrow.. so lets cap it at 24:00 ...
              localEndTimeMinutes = 1440;
            }

            let startTimeHours = Math.floor(localStartTimeMinutes / 60);
            let endTimeHours = Math.floor(localEndTimeMinutes / 60);

            startTimeMinutes = localStartTimeMinutes - startTimeHours * 60;
            endTimeMinutes = localEndTimeMinutes - endTimeHours * 60;

            const startTimePM = startTimeHours >= 12;
            const endTimePM = endTimeHours >= 12;

            if (startTimePM) {
              startTimeHours -= 12;
            }

            if (endTimePM) {
              endTimeHours -= 12;
            }

            if (startTimeHours === 0) {
              startTimeHours = 12;
            }

            if (endTimeHours === 0) {
              endTimeHours = 12;
            }

            period.formattedStartTime = startTimeMinutes
              ? `${startTimeHours}:${startTimeMinutes} ${
                  startTimePM ? 'PM' : 'AM'
                }`
              : `${startTimeHours} ${startTimePM ? 'PM' : 'AM'}`;
            period.formattedEndTime = endTimeMinutes
              ? `${endTimeHours}:${endTimeMinutes} ${endTimePM ? 'PM' : 'AM'}`
              : `${endTimeHours} ${endTimePM ? 'PM' : 'AM'}`;
          });
        });
      })
      .catch((ex) => {
        console.error(ex);
      });
  },
  methods: {
    sendMessage(filters) {
      this.$refs.sendMessageModal.show(this.counselor.user.id);
    },
    scheduleWithCounselor() {
      if (this.$store.getters.currentUser.num_pending_payments > 0) {
        return this.$store.commit('setGlobalAlert', {
          msg: 'To schedule a session you must first complete your pending payments.',
          error: true
        });
      }
      const c = {
        id: this.counselor.id,
        userId: this.counselor.user.id,
        name: this.counselor.user.fullName,
      }
      this.$modal.show(
        SessionRequestModal,
        {
          props: { counselor: c },
          listeners: {
            onerror: (err) => {
              console.error(err);
            },
            onsendamessage: (counselor) => {
              this.$refs.sendMessageModal.show(counselor.userId);
            },
            onrequest: (requestResult) => {
              this.$gtag.event('appointment_request', {
                student_id: requestResult.user.id,
                counselor_id: requestResult.counselor_id,
                start_time: requestResult.start_time,
              });
              this.$router.push({ name: 'dashboard', params: { from: 'event-scheduled' } });
            }
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.profile {
  .vacation {
    text-align: center;
    padding: 1rem;
    background-color: $off-green !important;
    border: 1px solid $green;
    border-radius: 5px;
    font-size: 1.15rem;
    p {
      margin: 0;
    }
    margin-bottom: 20px;
  }
  .avatar {
    text-align: center;

    .user-avatar {
      @include background-cover;
      @include border-radius(100px);

      background-image: url('~@/assets/img/default-user.jpg');
      height: 150px;
      width: 150px;
      margin-right: 15px;
      display: inline-block;
      vertical-align: top;
      margin-bottom: 15px;
    }

    p {
      opacity: 0.5;
    }
  }

  .info-list {
    list-style-type: none;
    padding-left: 0;
    font-size: 18px;
    margin-bottom: 0;

    i,
    svg {
      color: $green;
      margin-right: 8px;
    }

    button {
      font-size: 18px;

      i,
      svg {
        margin-right: 8px;
        font-size: 18px;
        position: relative;
        top: 1px;
      }
    }

    li {
      margin-bottom: 5px;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    a {
      color: white;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .pills {
    margin-bottom: 0;
    margin-top: 15px;
  }

  .appt-table {
    h2 {
      color: $green;
      font-size: 28px;
      margin-bottom: 20px;
    }
  }

  .insurance-block {
    label {
      @include label;

      margin-bottom: 15px;
    }

    p {
      font-size: 18px;
      margin-bottom: 0;
    }
  }

  .icon-btn {
    border: none;
    background: none;
    padding: 0;
    color: $green;
    font-size: 20px;
    margin-right: 20px;

    .fa-calendar-plus {
      font-size: 18px;
    }

    .fa-envelope,
    .fa-phone-square {
      font-size: 20px;
      position: relative;
      top: 2px;
    }
  }
}
</style>
